import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { App as CapacitorApp } from "@capacitor/app";

// ─── Capgo OTA — Instant Auto-Apply ──────────────────────────────────────
// Industry-standard "CodePush IMMEDIATE" pattern: as soon as a new bundle
// finishes downloading in the background, we activate + reload it within
// ~1 second. The user sees a single ~300ms webview refresh and is now on
// the latest code.  No force-quits.  No prompts.  No background/foreground
// dance.  Real users get updates the same way they get Instagram updates.
//
// Why this is safe:
//   • notifyAppReady() is called first → Capgo marks the *current* bundle
//     healthy, so even if our reload crashes the new bundle Capgo will
//     auto-rollback to the previous one within 30 seconds.
//   • We delay 800 ms after `updateAvailable` so any in-flight render
//     finishes before the swap (avoids a flash mid-animation).
//   • If the swap happens to coincide with the user mid-tap, the worst
//     case is a re-render of the same screen — no data loss because all
//     persistent state lives in MongoDB / localStorage.
// ─────────────────────────────────────────────────────────────────────────

let _swapping = false;

const swapToBundle = async (bundleId, version) => {
  if (_swapping || !bundleId) return;
  _swapping = true;
  try {
    // eslint-disable-next-line no-console
    console.log("[Capgo] Activating bundle", version, "→ instant reload");
    await CapacitorUpdater.set({ id: bundleId });
    // Tiny delay so set() fully commits before WebView reload
    await new Promise((r) => setTimeout(r, 300));
    await CapacitorUpdater.reload();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[Capgo] Bundle swap failed, falling back to hard reload:", err);
    try { window.location.reload(); } catch { /* noop */ }
  }
};

// 1. Mark current bundle healthy (stops Capgo 30 s rollback timer)
CapacitorUpdater.notifyAppReady().catch(() => { /* noop on web */ });

// 2. Listen for any newly-downloaded bundle and immediately activate + reload
CapacitorUpdater.addListener("updateAvailable", (evt) => {
  const bundleId = evt?.bundle?.id;
  const version = evt?.bundle?.version;
  if (!bundleId) return;
  // 800 ms window lets the user finish whatever tap/animation is in flight
  setTimeout(() => swapToBundle(bundleId, version), 800);
}).catch(() => { /* noop on web */ });

// 3. If user keeps the app open for days, re-poll every time it returns
//    to the foreground.  Capgo's native layer also auto-checks, but this
//    is a cheap re-arm.
CapacitorApp.addListener("appStateChange", async ({ isActive }) => {
  if (isActive) {
    try { await CapacitorUpdater.notifyAppReady(); } catch { /* noop */ }
  }
}).catch(() => { /* noop on web */ });

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
